/**
 * Блок «Переписка» (тикет T18b, спецификация § 3, § 5).
 *
 * Показывает Сообщения того Канала, на экране которого стоит, — своих настроек у Типа нет.
 * Лента читается снизу вверх, как её и читают: последнее Сообщение внизу, и экран сам
 * прокручивается к нему.
 *
 * Имя автора берётся из списка Участников, а не пишется в Сообщение сервером: человек
 * меняет имя, и тогда старые Сообщения остались бы подписаны прежним.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { FormEvent, ReactNode } from 'react';
import { ApiError } from '../net/api.js';
import type { Api, Member, Message } from '../net/api.js';
import { listenLive } from '../net/lenta.js';
import { Button, Notice } from '../ui/controls.js';
import { useSettings } from '../state/settings.js';

export function Conversation({
  api,
  channelId,
  members,
  meId,
  onRead,
}: {
  readonly api: Api;
  readonly channelId: string;
  readonly members: readonly Member[];
  /**
   * Кто смотрит. Правка и уборка предлагаются только на своём Сообщении: чужое не правит
   * никто, включая Администратора, и показывать ему кнопку, которая всегда откажет, — врать.
   */
  readonly meId?: string | undefined;
  /** «Дочитано, осталось столько-то» — чтобы счётчик в списке Каналов гас сразу. */
  readonly onRead?: ((channelId: string, unread: number) => void) | undefined;
}): ReactNode {
  const { t, appearance } = useSettings();
  const [messages, setMessages] = useState<readonly Message[]>([]);
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(true);
  const [refusal, setRefusal] = useState<string | null>(null);
  /** Какое Сообщение сейчас правят и что в поле правки. */
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingText, setEditingText] = useState('');
  const bottom = useRef<HTMLDivElement>(null);
  /** Та самая полоса, которая прокручивается: у неё и спрашиваем, внизу ли человек. */
  const laneRef = useRef<HTMLDivElement>(null);
  /** Ленту уже показали хоть раз — значит человек мог отлистать её сам. */
  const settled = useRef(false);
  /** До какого номера уже отметились прочитанным — чтобы не слать одно и то же дважды. */
  const marked = useRef(0);

  const load = useCallback(
    async (alive: () => boolean): Promise<void> => {
      setLoading(true);
      try {
        const said = await api.messages(channelId);
        // Ответ пришёл, когда Канал уже сменили, — выбрасываем. Иначе в новом Канале
        // мелькнёт чужая переписка: она приехала позже, а значит легла бы поверх.
        if (!alive()) return;
        setMessages(said);
        setRefusal(null);
      } catch (error) {
        if (!alive()) return;
        setRefusal(error instanceof Error ? error.message : t('messages.failed'));
      } finally {
        if (alive()) setLoading(false);
      }
    },
    [api, channelId, t],
  );

  useEffect(() => {
    let current = true;
    void load(() => current);
    return () => {
      current = false;
    };
  }, [load]);

  // Написали, поправили или убрали Сообщение в этом Канале — лента меняется сама, без
  // перезагрузки страницы. Спрашиваем ленту целиком, а не приклеиваем пришедшее: сервер
  // отдаёт её в своём порядке и уже отфильтрованной по правам, и повторять эту работу
  // в оболочке значило бы разойтись с ним.
  useEffect(() => {
    let current = true;
    const stop = listenLive(api.spaceSlug, (event) => {
      if (
        event.type !== 'message.created' &&
        event.type !== 'message.edited' &&
        event.type !== 'message.removed'
      ) {
        return;
      }
      if (event.payload['channelId'] !== channelId) return;
      void load(() => current);
    });
    return () => {
      current = false;
      stop();
    };
  }, [api, channelId, load]);

  useEffect(() => {
    // Экран уезжает к последнему Сообщению только если человек и так внизу. Утащить
    // ленту у того, кто отлистал назад и читает вчерашнее, — потерять ему место, а новое
    // Сообщение он и так увидит, когда вернётся вниз.
    // Спрашиваем именно ту полосу, которая прокручивается. Возьми мы соседа по разметке —
    // у него прокрутки нет, расстояние до низа всегда выходило бы нулевым, и «человек
    // внизу» оказалось бы правдой всегда, то есть проверки не было бы вовсе.
    const lane = laneRef.current;
    if (!lane) return;
    // Первый показ ленты — всегда вниз: разговор читают с конца, и открыть его на
    // позапрошлой неделе значит заставить человека листать до сегодняшнего дня руками.
    const first = !settled.current;
    settled.current = true;
    const distance = lane.scrollHeight - lane.scrollTop - lane.clientHeight;
    // Пара строк допуска: человек редко стоит ровно на нуле, а «почти внизу» — это внизу.
    if (!first && distance > 80) return;
    bottom.current?.scrollIntoView({
      behavior: appearance.motion === 'reduced' ? 'auto' : 'smooth',
      block: 'end',
    });
  }, [messages, appearance.motion]);

  // Открытый на экране Канал считается прочитанным до последнего Сообщения. Отметку шлём
  // только когда она выросла: сервер двигает её лишь вперёд, но гонять запрос на каждую
  // перерисовку значило бы стучаться в сеть просто так.
  useEffect(() => {
    const dochital = messages.reduce((most, one) => Math.max(most, one.channelSeq), 0);
    if (dochital === 0 || dochital <= marked.current) return;
    marked.current = dochital;
    void api
      .markRead(channelId, dochital)
      .then((left) => onRead?.(channelId, left))
      // Отметка не дошла — молчим. Сказать «не удалось отметить прочитанным» посреди
      // разговора значит напугать человека тем, чего он не просил; в следующий раз
      // отметка уйдёт заново, а сервер всё равно берёт наибольшее из двух.
      .catch(() => undefined);
  }, [messages, api, channelId, onRead]);

  // Сменили Канал — прежняя отметка к нему не относится, иначе в новом Канале с меньшими
  // номерами она оказалась бы «уже выше» и прочитанным он не пометился бы никогда.
  useEffect(() => {
    marked.current = 0;
    setEditingId(null);
  }, [channelId]);

  const nameOf = (memberId: string): string =>
    members.find((one) => one.id === memberId)?.displayName ?? memberId;

  const at = (moment: string): string => {
    const when = new Date(moment);
    if (Number.isNaN(when.getTime())) return '';
    return new Intl.DateTimeFormat(appearance.language, {
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: appearance.timeFormat === '12h' ? 'h12' : 'h23',
    }).format(when);
  };

  const send = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    const said = text.trim();
    if (!said) return;
    setText('');
    try {
      const message = await api.postMessage(channelId, said);
      setMessages((was) => [...was, message]);
      setRefusal(null);
    } catch (error) {
      // Написанное возвращаем в поле: потерять чужой текст из-за оборванной связи нельзя.
      setText(said);
      setRefusal(error instanceof Error ? error.message : t('messages.failed'));
    }
  };

  const startEdit = (message: Message): void => {
    setEditingId(message.id);
    setEditingText(message.body);
    setRefusal(null);
  };

  const saveEdit = async (message: Message): Promise<void> => {
    const said = editingText.trim();
    // Пустой правкой Сообщение не стирают — для этого есть «Убрать», и оно оставляет
    // пометку. Молча превратить реплику в пустое место значило бы потерять её без следа.
    if (!said || said === message.body) {
      setEditingId(null);
      return;
    }
    try {
      const fresh = await api.editMessage(message.id, said, message.revision);
      setMessages((was) => was.map((one) => (one.id === fresh.id ? fresh : one)));
      setEditingId(null);
      setRefusal(null);
    } catch (error) {
      // Опоздавшая правка. Показываем свежий текст и оставляем поле открытым: человек
      // видит, что успели написать без него, и решает сам — а не теряет свои слова.
      if (error instanceof ApiError && error.code === 'message_edit_conflict') {
        setRefusal(t('messages.editConflict'));
        void load(() => true);
        return;
      }
      setRefusal(error instanceof Error ? error.message : t('messages.failed'));
    }
  };

  const remove = async (message: Message): Promise<void> => {
    if (!globalThis.confirm(t('messages.removeConfirm'))) return;
    try {
      const stone = await api.removeMessage(message.id);
      setMessages((was) => was.map((one) => (one.id === stone.id ? stone : one)));
      setRefusal(null);
    } catch (error) {
      setRefusal(error instanceof Error ? error.message : t('messages.failed'));
    }
  };

  return (
    <div className="flex h-full min-h-0 flex-col gap-2">
      {refusal ? (
        <Notice kind="error" onClose={() => setRefusal(null)} closeLabel={t('common.close')}>
          {refusal}
        </Notice>
      ) : null}

      <div ref={laneRef} className="min-h-0 flex-1 overflow-auto">
        {loading ? <p className="text-ink-soft">{t('common.loading')}</p> : null}
        {!loading && messages.length === 0 ? (
          <p className="text-ink-soft">{t('messages.empty')}</p>
        ) : null}

        <ol className="flex flex-col gap-2">
          {messages.map((message) => {
            const gone = message.kind === 'removed';
            const mine = meId !== undefined && message.authorId === meId;
            return (
              <li key={message.id} className="flex flex-col">
                <span className="text-xs text-ink-soft">
                  {nameOf(message.authorId)} ·{' '}
                  <time dateTime={message.createdAt}>{at(message.createdAt)}</time>
                  {/* Пометка о правке — словом, а не значком: значок диктор не прочтёт,
                      а человеку с плохим зрением его ещё и не разглядеть. */}
                  {!gone && message.editedAt ? <> · {t('messages.edited')}</> : null}
                </span>

                {gone ? (
                  // Надгробие. Текста тут нет и быть не должно, но место в разговоре
                  // остаётся за ним: пропади реплика совсем — соседние теряют смысл.
                  <span className="italic text-ink-soft">{t('messages.removed')}</span>
                ) : editingId === message.id ? (
                  <div className="flex flex-col gap-1">
                    <label>
                      {/* Диктору называем и время: в длинной ленте «изменение Сообщения»
                          само по себе не говорит, какое именно взято в правку. */}
                      <span className="ktm-only-voice">
                        {t('messages.editing')} — {at(message.createdAt)}
                      </span>
                      <textarea
                        value={editingText}
                        rows={2}
                        autoFocus
                        onChange={(event) => setEditingText(event.currentTarget.value)}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter' && !event.shiftKey) {
                            event.preventDefault();
                            void saveEdit(message);
                          }
                          // Escape возвращает как было — тем же движением, что и везде
                          // в этой оболочке: передумать должно быть так же легко, как начать.
                          if (event.key === 'Escape') setEditingId(null);
                        }}
                        className="w-full resize-none rounded-md border border-line bg-surface px-2 py-1 text-ink"
                      />
                    </label>
                    <div className="flex gap-2">
                      <Button type="button" look="primary" onClick={() => void saveEdit(message)}>
                        {t('messages.editSave')}
                      </Button>
                      <Button type="button" onClick={() => setEditingId(null)}>
                        {t('messages.editCancel')}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <span className="whitespace-pre-wrap break-words">{message.body}</span>
                )}

                {/* Кнопки только на своём и только пока Сообщение живо. Чужое не правит
                    никто, включая Администратора, — кнопка, которая всегда откажет, врёт. */}
                {mine && !gone && editingId !== message.id ? (
                  <span className="flex gap-2 text-xs">
                    <button
                      type="button"
                      onClick={() => startEdit(message)}
                      className="text-ink-soft underline underline-offset-2 hover:text-ink"
                    >
                      {t('messages.edit')}
                    </button>
                    <button
                      type="button"
                      onClick={() => void remove(message)}
                      className="text-ink-soft underline underline-offset-2 hover:text-ink"
                    >
                      {t('messages.remove')}
                    </button>
                  </span>
                ) : null}
              </li>
            );
          })}
        </ol>
        <div ref={bottom} />
      </div>

      <form onSubmit={send} className="flex shrink-0 items-end gap-2">
        <label className="flex-1">
          <span className="ktm-only-voice">{t('messages.write')}</span>
          <textarea
            value={text}
            rows={2}
            placeholder={t('messages.write')}
            onChange={(event) => setText(event.currentTarget.value)}
            onKeyDown={(event) => {
              // Enter отправляет, Shift+Enter переносит строку — так пишут во всех
              // мессенджерах, и переучивать человека здесь незачем.
              if (event.key === 'Enter' && !event.shiftKey) {
                event.preventDefault();
                void send(event as unknown as FormEvent);
              }
            }}
            className="w-full resize-none rounded-md border border-line bg-surface px-2 py-1 text-ink"
          />
        </label>
        <Button type="submit" look="primary" disabled={!text.trim()}>
          {t('messages.send')}
        </Button>
      </form>
    </div>
  );
}
